import { ChevronDownIcon, ChevronRightIcon, theme } from '@arnold/common';
import { removeAccents } from '@arnold/core';
import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckboxRow } from './CheckboxRow';
import { EmptySearchResult } from './EmptySearchResult';
import { HighlightText } from './HighlightText';

export interface QuestionOption {
  id: string;
  label: string;
}

export interface QuestionStatement {
  id: string;
  label: string;
}

export interface QuestionItem {
  id: string;
  text: string;
  options: QuestionOption[];
  statements?: QuestionStatement[];
}

export interface AnswerSelection {
  questionId: string;
  optionId: string;
  statementId?: string;
}

interface QuestionFilterContentProps {
  questions: QuestionItem[];
  selectedAnswers: AnswerSelection[];
  onToggleAnswer: (questionId: string, optionId: string, statementId?: string) => void;
  searchQuery: string;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

const getAnswerSelectionKey = ({ questionId, optionId, statementId }: AnswerSelection): string =>
  `${questionId}:${statementId ?? ''}:${optionId}`;

function filterQuestions(questions: QuestionItem[], query: string): QuestionItem[] {
  if (!query) return questions;
  const normalized = removeAccents(query).toLowerCase();
  return questions
    .map((q) => {
      const questionMatches = removeAccents(q.text).toLowerCase().includes(normalized);
      const matchingOptions = q.options.filter((o) => removeAccents(o.label).toLowerCase().includes(normalized));
      const matchingStatements = q.statements?.filter((s) => removeAccents(s.label).toLowerCase().includes(normalized));
      if (questionMatches) return q;
      if (matchingOptions.length > 0) return { ...q, options: matchingOptions };
      if (matchingStatements && matchingStatements.length > 0) return { ...q, statements: matchingStatements };
      return null;
    })
    .filter(Boolean) as QuestionItem[];
}

export function useQuestionExpansion(questions: QuestionItem[]) {
  const allIds = useMemo(() => questions.map((q) => q.id), [questions]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(allIds));
  const prevAllIdsRef = useRef<string[]>([]);

  useEffect(() => {
    const prevAllIds = new Set(prevAllIdsRef.current);
    prevAllIdsRef.current = allIds;

    setExpandedIds((prev) => {
      if (allIds.length === 0) {
        return new Set();
      }

      if (prev.size === 0) {
        return new Set(allIds);
      }

      const next = new Set<string>();
      for (const id of allIds) {
        if (prev.has(id) || !prevAllIds.has(id)) {
          next.add(id);
        }
      }
      return next;
    });
  }, [allIds]);

  const allExpanded = allIds.length > 0 && expandedIds.size >= allIds.length;

  const toggleExpandAll = useCallback(() => {
    setExpandedIds(allExpanded ? new Set() : new Set(allIds));
  }, [allExpanded, allIds]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAllForSearch = useCallback((filteredQuestions: QuestionItem[]) => {
    setExpandedIds(new Set(filteredQuestions.map((q) => q.id)));
  }, []);

  return { expandedIds, allExpanded, toggleExpandAll, toggleExpand, expandAllForSearch };
}

export const QuestionFilterContent = ({
  questions,
  selectedAnswers,
  onToggleAnswer,
  searchQuery,
  expandedIds,
  onToggleExpand,
}: QuestionFilterContentProps) => {
  const filteredQuestions = useMemo(() => filterQuestions(questions, searchQuery), [questions, searchQuery]);

  const selectedSet = useMemo(() => new Set(selectedAnswers.map(getAnswerSelectionKey)), [selectedAnswers]);

  if (filteredQuestions.length === 0 && searchQuery) {
    return <EmptySearchResult />;
  }

  return (
    <>
      {filteredQuestions.map((question) => {
        const isExpanded = expandedIds.has(question.id);
        return (
          <div key={question.id}>
            <QuestionHeader type="button" aria-expanded={isExpanded} onClick={() => onToggleExpand(question.id)}>
              <ChevronWrapper>{isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}</ChevronWrapper>
              <QuestionText>
                <HighlightText text={question.text} query={searchQuery} />
              </QuestionText>
            </QuestionHeader>
            {isExpanded &&
              (question.statements ? (
                <StatementsContainer>
                  {question.statements.map((statement) => (
                    <div key={statement.id}>
                      <StatementText>{<HighlightText text={statement.label} query={searchQuery} />}</StatementText>
                      <OptionsContainer>
                        {question.options.map((option) => {
                          const isSelected = selectedSet.has(
                            getAnswerSelectionKey({
                              questionId: question.id,
                              optionId: option.id,
                              statementId: statement.id,
                            }),
                          );
                          return (
                            <CheckboxRow
                              key={option.id}
                              selected={isSelected}
                              label={<HighlightText text={option.label} query={searchQuery} />}
                              onClick={() => {
                                onToggleAnswer(question.id, option.id, statement.id);
                              }}
                              level={1}
                              isStatement
                            />
                          );
                        })}
                      </OptionsContainer>
                    </div>
                  ))}
                </StatementsContainer>
              ) : (
                <div style={{ paddingLeft: '38px' }}>
                  <OptionsContainer>
                    {question.options.map((option) => {
                      const isSelected = selectedSet.has(
                        getAnswerSelectionKey({ questionId: question.id, optionId: option.id }),
                      );
                      return (
                        <CheckboxRow
                          key={option.id}
                          selected={isSelected}
                          label={<HighlightText text={option.label} query={searchQuery} />}
                          onClick={() => {
                            onToggleAnswer(question.id, option.id);
                          }}
                          level={1}
                        />
                      );
                    })}
                  </OptionsContainer>
                </div>
              ))}
          </div>
        );
      })}
    </>
  );
};

const QuestionHeader = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: ${theme.spacing.e} ${theme.spacing.g};
  padding-left: ${theme.spacing.d};
  text-align: left;
  font: inherit;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: ${theme.colors.backgroundBasic.default};
  }

  &:focus-visible {
    outline: ${theme.spacing.a} solid ${theme.colors.brand.primary};
    border-radius: ${theme.spacing.c};
  }
`;

const ChevronWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: ${theme.spacing.b};
  margin-top: 2px;
`;

const QuestionText = styled.span<{ $bold?: boolean }>`
  font-weight: ${({ $bold }) => ($bold ? 700 : 400)};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  line-height: ${theme.typography.body.medium.regular.lineHeight};
  margin-left: ${theme.spacing.d};
`;

const OptionsContainer = styled.div`
  padding-bottom: ${theme.spacing.d};
`;

const StatementsContainer = styled.div`
  padding: ${theme.spacing.e} ${theme.spacing.g};
  padding-left: 42px;
`;

const StatementText = styled.span`
  font-size: ${theme.typography.body.medium.regular.fontSize};
  line-height: ${theme.typography.body.medium.regular.lineHeight};
`;
